'use client'

import { avatarColorFromName, avatarInitials } from '@/components/shared/ui/base/avatar/avatar-color'
import { Avatar, type AvatarProps } from '@/components/shared/ui/base/avatar/avatar'
import { Button as AriaButton, Tooltip as AriaTooltip, TooltipTrigger as AriaTooltipTrigger } from 'react-aria-components'
import { cx } from '@/utils/cx'

export interface AvatarStackItem {
  name: string
  key?: string
}

interface AvatarStackProps {
  items?: AvatarStackItem[]
  maxVisible?: number
  /** Pixel size (kept for backwards-compat) or an Avatar size token. */
  size?: number | AvatarProps['size']
  popoverTitle?: string
  className?: string
  /** @deprecated kept for backwards-compat; overlap is now fixed via ring spacing. */
  overlap?: number
  /** @deprecated kept for backwards-compat; initials are derived from the name. */
  letters?: number
}

const SIZE_TOKEN: Record<number, AvatarProps['size']> = {
  24: 'xs',
  32: 'sm',
  40: 'md',
  48: 'lg',
}

export function AvatarStack({
  items = [],
  maxVisible = 3,
  size = 'sm',
  popoverTitle = 'Items',
  className,
}: AvatarStackProps) {
  const safeItems = items.filter((it) => it && it.name)
  const visible = safeItems.slice(0, maxVisible)
  const extra = safeItems.length - visible.length

  const avatarSize: AvatarProps['size'] = typeof size === 'number' ? (SIZE_TOKEN[size] ?? 'sm') : size

  if (safeItems.length === 0) return null

  return (
    <AriaTooltipTrigger delay={300} closeDelay={0}>
      <AriaButton className="h-max w-max outline-hidden">
        <div className={cx('flex items-center -space-x-2', className)}>
          {visible.map((it, i) => {
            const initials = avatarInitials(it.name)
            return (
              <Avatar
                key={it.key ?? `${it.name}-${i}`}
                size={avatarSize}
                alt={it.name}
                initials={initials}
                contentClassName={initials ? avatarColorFromName(it.name) : undefined}
                className="ring-2 ring-bg-primary"
              />
            )
          })}

          {extra > 0 && (
            <Avatar
              size={avatarSize}
              initials={`+${extra}`}
              contentClassName="bg-quaternary text-secondary"
              className="ring-2 ring-bg-primary"
            />
          )}
        </div>
      </AriaButton>

      <AriaTooltip
        offset={6}
        placement="top"
        className={({ isEntering, isExiting }) =>
          cx(isEntering && 'ease-out animate-in', isExiting && 'ease-in animate-out')
        }
      >
        <div className="border-secondary bg-primary z-50 flex max-w-xs flex-col gap-1.5 rounded-lg border px-3 py-2 shadow-lg">
          <span className="text-secondary text-xs font-semibold">{popoverTitle}</span>
          <div className="border-secondary -mx-3 border-t" />
          <ul className="flex flex-col gap-1.5">
            {safeItems.map((it, i) => {
              const initials = avatarInitials(it.name)
              return (
                <li key={it.key ?? `${it.name}-${i}`} className="flex items-center gap-2">
                  <Avatar
                    size="xs"
                    alt={it.name}
                    initials={initials}
                    contentClassName={initials ? avatarColorFromName(it.name) : undefined}
                  />
                  <span className="text-primary text-xs font-medium">{it.name}</span>
                </li>
              )
            })}
          </ul>
        </div>
      </AriaTooltip>
    </AriaTooltipTrigger>
  )
}
